import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { NB_WINDOW_CONTEXT } from '@nebular/theme'
import { FFService } from '../../ff/ff.service'
import { AuthenticationService } from '../../ff/services/authentication/authentication.service'
import { country_codes } from './country_codes'
import { timezones } from './timezones'

/**
 * Custom Validators
 */
export function no_trailing_space_validator ( control: AbstractControl ): ValidationErrors | null {
  return ( typeof control.value === 'string' && /\s$/.test( control.value ) )
    ? { trailing_space: true }
    : null
}
const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ'’\-\s.]{1,50}$/u
export function name_validator ( control: AbstractControl ): ValidationErrors | null {
  const value = ( control.value || '' ).trim()
  if ( !value ) return { required: true }
  if ( !NAME_REGEX.test( value ) ) return { invalid_name: true }
  return null
}
const PHONE_REGEX = /^[+]?[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\.\/0-9]*$/
export function phone_validator ( control: AbstractControl ): ValidationErrors | null {
  const value = ( control.value || '' ).trim()
  if ( !value ) return { required: true }
  if ( !PHONE_REGEX.test( value ) ) return { invalid_phone: true }
  if ( value.length < 7 || value.length > 20 ) return { invalid_length: true }
  return null
}

@Component( {
  selector: 'ff-edit-user',
  templateUrl: './edit_user.component.html',
  styleUrls: ['./edit_user.component.scss']
} )
export class EditUserComponent implements OnInit {
  edit_user_form: FormGroup

  namespace: string = '071cbad7-d14e-4a22-8606-dbb645deb5fb';

  redirectDelay: number = 0; strategy: string = '';

  errors: string[] = [];
  messages: string[] = [];

  available_roles_teams: string[] = [
    'executive',
    'operations',
    'investigator',
    'account_administrator',
    'site_administrator'
    // Add more as needed
  ]
  phone_number_types = [
    'unknown',
    'declined_to_provide',
    'mobile',
    'office',
    'home',
  ];

  ff: any

  country_codes = country_codes;
  timezones = timezones;

  submitted: boolean = false;
  rememberMe = false;

  is_new_password: boolean

  user: any = {};

  constructor(
    @Inject( NB_WINDOW_CONTEXT ) protected context: any,
    public authentication_service: AuthenticationService,
    protected cd: ChangeDetectorRef,
    protected router: Router,
    private ff_service: FFService,
    private fb: FormBuilder
  ) {
    this.ff = this.ff_service.ff
    this.user = context.user
  }

  ngOnInit () {
    // Ensure nested objects exist
    this.user.address = this.user.address || {}
    this.user.phone_number = this.user.phone_number || {}

    // Set timezone (prefer user, else browser)
    if ( !this.user.timezone ) {
      this.user.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    // Set country code (prefer user, else browser)
    if ( !this.user.address.country_iso_3116_1 ) {
      const locale = navigator.language || ( navigator as any ).userLanguage || 'en-US'
      if ( locale && locale.includes( '-' ) ) {
        this.user.address.country_iso_3116_1 = locale.split( '-' )[1].toLowerCase()
      } else {
        this.user.address.country_iso_3116_1 = 'us'
      }
    }

    // --- Strong Validators: match create_user.component.ts style ---
    this.edit_user_form = this.fb.group( {
      // user_contact_email is not editable here for security
      first_name: [this.user.first_name || '', [
        name_validator,
        no_trailing_space_validator
      ]],
      last_name: [this.user.last_name || '', [
        name_validator,
        no_trailing_space_validator
      ]],
      new_password: ['', [
        Validators.minLength( 12 ),
        Validators.maxLength( 100 ),
        no_trailing_space_validator
      ]], // only required if password is being set
      roles: [this.user.roles || [], Validators.required],
      teams: [this.user.teams || []],
      timezone: [this.user.timezone || '', Validators.required],
      country_iso_3116_1: [this.user.address.country_iso_3116_1 || '', Validators.required],
      phone_number_type: [this.user.phone_number.phone_number_type || 'unknown', Validators.required],
      phone_number: [this.user.phone_number.phone_number || '', phone_validator],
      is_new_password: [false, Validators.required],
      is_enabled: [this.user.is_enabled !== undefined ? this.user.is_enabled : true, Validators.required],
      mfa_required: [this.user.mfa_required !== undefined ? this.user.mfa_required : true, Validators.required],
    } )

    this.is_new_password = false

    console.log( 'DEBUG edit_user() ngOnInit() USER=', this.user )
  }

  // Edit a user profile.
  edit_user (): void {
    if ( !this.edit_user_form.valid ) {
      console.log( 'user form is invalid', this.edit_user_form.value )
      return
    }

    this.errors = []
    this.messages = []
    this.submitted = true

    // mongodb remnant
    delete this.user['_id']

    // --- Normalization/cleanup ---
    const new_first_name = ( this.edit_user_form.value.first_name || '' ).trim()
    const new_last_name = ( this.edit_user_form.value.last_name || '' ).trim()
    const new_phone_number = ( this.edit_user_form.value.phone_number || '' ).trim()

    this.user = {
      ...this.user,
      user_contact_email: this.user.user_contact_email, // keep original
      first_name: new_first_name,
      last_name: new_last_name,
      new_password: this.edit_user_form.value.new_password,
      roles: this.edit_user_form.value.roles,
      teams: this.edit_user_form.value.teams,
      timezone: this.edit_user_form.value.timezone,
      address: {
        country_iso_3116_1: this.edit_user_form.value.country_iso_3116_1
      },
      phone_number: {
        phone_number_type: this.edit_user_form.value.phone_number_type,
        phone_number: new_phone_number
      },
      is_enabled: this.edit_user_form.value.is_enabled,
      mfa_required: this.edit_user_form.value.mfa_required,
      mime_type: 'access_control/account'
    }

    // Remove password if not being set
    if ( !this.edit_user_form.value.is_new_password ) {
      delete this.user.new_password
    }

    // roles normalization
    if ( typeof this.user.roles === 'string' ) {
      this.user.roles = this.user.roles.split( ',' ).map( ( x: string ) => x.trim() ).filter( Boolean )
    }
    if ( !Array.isArray( this.user.roles ) ) {
      this.user.roles = []
    }
    // teams normalization
    if ( typeof this.user.teams === 'string' ) {
      this.user.teams = this.user.teams.split( ',' ).map( ( x: string ) => x.trim() ).filter( Boolean )
    }
    if ( !Array.isArray( this.user.teams ) ) {
      this.user.teams = []
    }

    // Call backend to update
    const response = this.authentication_service.store( this.user )
    if ( response ) {
      response.subscribe( {
        next: ( res: any ) => {
          this.messages.push( 'Updated user profile' )
          if ( !Array.isArray( res ) ) res = [res]
          this.submitted = false
          this.authentication_service.get_current_user().subscribe()
        },
        error: ( err: any ) => {
          this.submitted = false
          this.errors.push( 'There was a problem updating your account.' )
          let error_data = err.error
          if ( error_data ) {
            if ( !Array.isArray( error_data ) ) {
              error_data = [error_data]
            }
            error_data.forEach( ( error: any ) => {
              if ( error.is_error ) {
                if ( error.short_description ) {
                  this.errors.push( error.short_description )
                }
              }
            } )
          } else {
            this.errors.push( 'There was a problem updating your account -- we did not get an acknowledgement from the server.' )
          }
        }, // error
      } )
    } else {
      this.submitted = false
    }
  }

}
